import type { DocumentGenerationJobSource } from '@tracedocuments/types';

import { getApiErrorMessage } from './errors';
import { apiFetch } from './fetch';
import { apiFetchWithTimeout } from './fetch-timeout';

export type GenerationJobStatus = 'Pending' | 'Processing' | 'Succeeded' | 'Failed' | 'Cancelled';

export type GenerationJobPhase =
  | 'Queued'
  | 'Embedding'
  | 'Watermarking'
  | 'Uploading'
  | 'Notifying';

export type GenerationJobDto = {
  id: string;
  documentId: string;
  status: GenerationJobStatus;
  source: DocumentGenerationJobSource;
  errorMessage?: string | null;
  correlationId?: string | null;
  createdAt: string;
  startedAt?: string | null;
  completedAt?: string | null;
  phase?: GenerationJobPhase;
  progressPercent?: number;
  processedRecipients?: number;
  totalRecipients?: number;
  documentName?: string | null;
};

export type ActiveGenerationJobDto = {
  jobId: string;
  documentId: string;
  documentName: string;
  status: GenerationJobStatus;
  source: DocumentGenerationJobSource;
  createdAt: string;
  startedAt?: string | null;
  phase?: GenerationJobPhase;
  progressPercent?: number;
};

export type EnqueueGenerationJobResponse = {
  jobId: string;
  status: GenerationJobStatus;
};

export async function enqueueGeneration(
  documentId: string,
  recipientIds?: string[],
): Promise<EnqueueGenerationJobResponse> {
  const res = await apiFetch(`/api/documents/${documentId}/generate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ recipientIds: recipientIds ?? null }),
  });
  if (!res.ok) {
    const message = await getApiErrorMessage(res);
    throw new Error(message ?? 'generation enqueue failed');
  }
  return res.json();
}

export async function regenerateAllCopies(
  documentId: string,
): Promise<EnqueueGenerationJobResponse> {
  const res = await apiFetch(`/api/documents/${documentId}/regenerate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
  if (!res.ok) {
    const message = await getApiErrorMessage(res);
    throw new Error(message ?? 'regeneration enqueue failed');
  }
  return res.json();
}

export type RecipientLinkDto = {
  recipientId: string;
  recipientName: string;
  recipientEmail: string;
  downloadUrl: string;
  expiresAt: string;
};

export async function resendRecipientDownloadEmail(
  recipientId: string,
): Promise<RecipientLinkDto> {
  const res = await apiFetch(`/api/recipients/${recipientId}/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
  if (!res.ok) {
    const message = await getApiErrorMessage(res);
    throw new Error(message ?? 'resend download email failed');
  }
  return res.json();
}

export async function fetchGenerationJob(jobId: string): Promise<GenerationJobDto> {
  const res = await apiFetchWithTimeout(`/api/generation-jobs/${jobId}`);
  if (!res.ok) throw new Error('generation job fetch failed');
  return res.json();
}

export async function fetchActiveGenerationJob(documentId: string): Promise<GenerationJobDto | null> {
  const res = await apiFetchWithTimeout(`/api/documents/${documentId}/generation-jobs/active`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error('active generation job fetch failed');
  return res.json();
}

export async function fetchActiveGenerationJobs(): Promise<ActiveGenerationJobDto[]> {
  const res = await apiFetchWithTimeout('/api/generation-jobs/active');
  if (!res.ok) throw new Error('active generation jobs fetch failed');
  return res.json();
}

export async function waitForGenerationJob(
  jobId: string,
  options?: { pollMs?: number; timeoutMs?: number },
): Promise<GenerationJobDto> {
  const pollMs = options?.pollMs ?? 3000;
  const timeoutMs = options?.timeoutMs ?? 600_000;
  const started = Date.now();

  for (;;) {
    const job = await fetchGenerationJob(jobId);
    if (job.status === 'Succeeded') return job;
    if (job.status === 'Failed' || job.status === 'Cancelled') {
      throw new Error(job.errorMessage ?? 'Generation failed');
    }
    if (Date.now() - started > timeoutMs) {
      throw new Error('Generation timed out');
    }
    await new Promise((r) => setTimeout(r, pollMs));
  }
}
